'use client';

import { useCallback, useEffect } from 'react';
import { Locale } from '@headless-workspace/config';
import {
    DataLayerClassName,
    DataLayerEvent,
    DataLayerEventCategory,
    DataLayerEventLabel,
    DataLayerKey,
    HeaderDidMount,
    useDataLayer,
} from '@headless-workspace/domain/common/client';
import { DataLayerUtils } from '@headless-workspace/domain/common/client';
import { Component } from '@headless-workspace/typings';

type HomeDidMountProps = {
    locale: Locale;
};

enum AnimationAction {
    FastAccess = 'fast access',
    Brandorama = 'brandorama',
    BeautyTips = 'beauty tips',
}

const DATA_LAYER_CONNECTION_ACTION = 'homepage login';

export const HomeDidMount: Component<HomeDidMountProps> = ({ locale }) => {
    const { trackEvent, addVariables, trackT2SEvent, deleteVariable } = useDataLayer({
        [DataLayerKey.EnvTemplate]: 'homepage',
        [DataLayerKey.PageName]: 'homepage',
        [DataLayerKey.PageTopCat]: 'homepage',
    });
    // Factory function
    const createAnimationClickTracker = useCallback(
        (action: AnimationAction) => (dataLayerId: string) => () =>
            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                category: DataLayerEventCategory.Animation,
                event: 'Animation',
                action,
                label: dataLayerId,
            }),
        [trackEvent],
    );

    const trackFastAccessClick = createAnimationClickTracker(AnimationAction.FastAccess);
    const trackBrandoramaClick = createAnimationClickTracker(AnimationAction.Brandorama);
    const trackBeautyTipsClick = createAnimationClickTracker(AnimationAction.BeautyTips);

    // Banner click  event tracking
    const trackBannerClick = useCallback(
        (bannerType: string, bannerId: string, bannerName: string) => {
            return () =>
                trackEvent(DataLayerEvent.Click, {
                    id: 'click',
                    category: DataLayerEventCategory.Animation,
                    action: bannerType,
                    label: [bannerId, bannerName],
                });
        },
        [trackEvent],
    );

    useEffect(() => {
        const eventClickHandlerReferences: [Element, EventListener][] = [];

        // Add event listeners for beauty tips
        DataLayerUtils.addEventListeners(
            `.${DataLayerClassName.beautyTips}`,
            (element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                return trackBeautyTipsClick(dataLayerId);
            },
            eventClickHandlerReferences,
        );

        // Add event listeners for fast access
        DataLayerUtils.addEventListeners(
            `.${DataLayerClassName.fastAccess}`,
            (element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                return trackFastAccessClick(dataLayerId);
            },
            eventClickHandlerReferences,
        );
        // Add event listeners for Brandorama
        DataLayerUtils.addEventListeners(
            `.${DataLayerClassName.brandorama}`,
            (element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                return trackBrandoramaClick(dataLayerId);
            },
            eventClickHandlerReferences,
        );

        // Add event listeners for nested category navigation
        DataLayerUtils.addEventListeners(
            `.${DataLayerClassName.banner}`,
            (element) => {
                const dataLayerId = element.getAttribute('data-layer-id') ?? DataLayerEventLabel.Null;
                const dataLayerName = element.getAttribute('data-layer-name') ?? DataLayerEventLabel.Null;
                const dataLayerType = element.getAttribute('data-layer-banner-action') ?? DataLayerEventLabel.Null;
                return trackBannerClick(dataLayerType, dataLayerId, dataLayerName);
            },
            eventClickHandlerReferences,
        );

        return () => {
            eventClickHandlerReferences.forEach(([element, handler]) => {
                element.removeEventListener('click', handler);
            });
        };
    }, [trackFastAccessClick, trackBrandoramaClick, trackBeautyTipsClick, trackBannerClick]);

    return (
        <HeaderDidMount
            dataLayerConnectionAction={DATA_LAYER_CONNECTION_ACTION}
            trackEvent={trackEvent}
            addVariables={addVariables}
            trackT2SEvent={trackT2SEvent}
            locale={locale}
            deleteVariable={deleteVariable}
        />
    );
};
